import {
  ContextValueSetters,
  NewOfferOfferSubmissionErrorsKey,
  OfferApiFunctions,
} from 'pages/PrivateOffers/pages/Next/generic/ApiContext/apiContext';
import { MsftPrivateOffer } from 'pages/PrivateOffers/pages/Next/msft/api/types/MsftPrivateOffer';
import { useCallback } from 'react';
import useOfferApi from 'pages/PrivateOffers/pages/Next/generic/hooks/useOfferApi';
import { msftPrivateOfferJSONToMsftPrivateOffer } from 'pages/PrivateOffers/pages/Next/msft/api/transformers/msftPrivateOfferJSONToMsftPrivateOffer';
import { OfferAPIKey } from 'pages/PrivateOffers/pages/Next/generic/ApiContext/offerAPIKey';
import {
  offersMsftApi,
  salesforceDataMapperApi,
} from 'pages/PrivateOffers/pages/Next/generic/api/offerAPIs';
import { PlanDetails } from 'pages/PrivateOffers/pages/Next/msft/api/types/PlanDetails';
import { msftPrivateOfferToMsftCreateOfferJSON } from 'pages/PrivateOffers/pages/Next/msft/api/transformers/msftPrivateOfferToMsftCreateOfferJSON';
import { offerHasPreviewFeatures } from './utils/offerApiUtils';
import { msftPrivateOfferToMsftCreateOfferPreviewJSON } from './transformers/msftPrivateOfferToMsftCreateOfferPreviewJSON';
import { msftPrivateOfferToMsftUpdateOfferJSON } from 'pages/PrivateOffers/pages/Next/msft/api/transformers/msftPrivateOfferToMsftUpdateOfferJSON';
import { msftPrivateOfferToMsftUpdatePreviewOfferJSON } from 'pages/PrivateOffers/pages/Next/msft/api/transformers/msftPrivateOfferToMsftUpdatePreviewOfferJSON';
import { offerLogger } from 'utils/monitor/datadog';
import ky from 'ky';
import { OfferType } from 'pages/PrivateOffers/pages/Next/generic/types/TackleOffer';
import { MsftPrivateOfferSalesforceDataMapping } from './types/MsftPrivateOfferSalesforceDataMapping';
import { MsftPrivateOfferSalesforceDataMappingResponseJSON } from './types/MsftPrivateOfferSalesforceDataMappingResponseJSON';
import { msftPrivateOfferSalesforceDataMappingJSONToMsftPrivateOfferSalesforceDataMapping } from 'pages/PrivateOffers/pages/Next/msft/api/transformers/msftPrivateOfferSalesforceDataMappingJSONToMsftPrivateOfferSalesforceDataMapping';
import {
  OfferState,
  OfferStatus,
} from 'pages/PrivateOffers/pages/Next/msft/api/types/MsftPrivateOfferSharedTypes';

type UseMsftPrivateOfferApi = OfferApiFunctions<MsftPrivateOffer> & {
  createOffer: (
    offer: Partial<MsftPrivateOffer>,
    createInMarketplace: boolean,
  ) => Promise<MsftPrivateOffer | null>;
  getPlans: (vendorId: string, productId: string) => Promise<any | null>;
  getPlan: (
    vendorId: string,
    productId: string,
    planId: string,
  ) => Promise<PlanDetails | null>;
  getSalesforceDataMapping: (
    offerType: OfferType,
    opportunityId: string,
    mappingId?: string,
  ) => Promise<MsftPrivateOfferSalesforceDataMapping | null>;
};

const convertToMsftPrivateOffer = async (response: any) =>
  msftPrivateOfferJSONToMsftPrivateOffer(response);

const convertToSalesforceDataMapping = async (
  responseJson: MsftPrivateOfferSalesforceDataMappingResponseJSON,
) =>
  msftPrivateOfferSalesforceDataMappingJSONToMsftPrivateOfferSalesforceDataMapping(
    responseJson,
  );

const useMsftPrivateOfferApi = (
  accessTokenProvider: () => Promise<string>,
  contextValueSetters: ContextValueSetters,
): UseMsftPrivateOfferApi => {
  const { setOfferSubmissionErrors } = contextValueSetters;

  const {
    stateTrackingGet,
    stateTrackingSubmit,
    stateTrackingSubmitWithEmptyResponse,
  } = useOfferApi(contextValueSetters);

  const handleOfferSubmissionError = useCallback(
    (poId: string) =>
      async (error: ky.HTTPError): Promise<null> => {
        offerLogger?.warn('a MSFT api error occurred', {
          api_error: {
            message: error.message,
            stack: error.stack,
            response: error.response,
          },
        });

        // TODO - swap this out with real error once we know what the shapes will look like
        const offerSubmissionError = {
          message: 'An internal server error occurred.',
        };

        setOfferSubmissionErrors((previousOfferSubmissionErrors) => ({
          ...previousOfferSubmissionErrors,
          [poId]: offerSubmissionError,
        }));

        return null;
      },
    [setOfferSubmissionErrors],
  );

  const getOffer = useCallback(
    async (poId: string): Promise<MsftPrivateOffer | null> => {
      return stateTrackingGet(
        OfferAPIKey.Offer,
        () => offersMsftApi(accessTokenProvider).get(`api/offer/${poId}`),
        convertToMsftPrivateOffer,
      );
    },
    [stateTrackingGet, accessTokenProvider],
  );

  const getOfferSilently = useCallback(
    async (poId: string) => {
      return stateTrackingGet(
        OfferAPIKey.Silent,
        () => offersMsftApi(accessTokenProvider).get(`api/offer/${poId}`),
        convertToMsftPrivateOffer,
      );
    },
    [stateTrackingGet, accessTokenProvider],
  );

  const createOffer = useCallback(
    async (
      offer: Partial<MsftPrivateOffer>,
      createInMarketplace: boolean,
    ): Promise<MsftPrivateOffer | null> => {
      const status = createInMarketplace
        ? OfferStatus.SubmitToCloudStatus
        : OfferStatus.DraftStatus;

      const state = createInMarketplace ? null : OfferState.DraftState;

      const offerWithStatusAndState: Partial<MsftPrivateOffer> = {
        ...offer,
        status,
        state,
      };

      const hasPreviewFeatures = offerHasPreviewFeatures(
        offerWithStatusAndState,
      );

      const json = hasPreviewFeatures
        ? msftPrivateOfferToMsftCreateOfferPreviewJSON(offerWithStatusAndState)
        : msftPrivateOfferToMsftCreateOfferJSON(offerWithStatusAndState);

      const url = hasPreviewFeatures
        ? 'api/offer/saas/preview'
        : 'api/offer/saas';

      offerLogger?.info('sending create MSFT offer request', { json, url });

      return stateTrackingSubmit(
        OfferAPIKey.Offer,
        () => offersMsftApi(accessTokenProvider).post(url, { json }),
        convertToMsftPrivateOffer,
        handleOfferSubmissionError(NewOfferOfferSubmissionErrorsKey),
      );
    },
    [stateTrackingSubmit, accessTokenProvider, handleOfferSubmissionError],
  );

  const updateOffer = useCallback(
    async (
      poId: string,
      updatedOffer: Partial<MsftPrivateOffer>,
      createInMarketplace: boolean,
    ) => {
      const status = createInMarketplace
        ? OfferStatus.SubmitToCloudStatus
        : OfferStatus.DraftStatus;

      const state = createInMarketplace ? null : OfferState.DraftState;

      const offerWithStatusAndState: Partial<MsftPrivateOffer> = {
        ...updatedOffer,
        status,
        state,
      };

      const hasPreviewFeatures = offerHasPreviewFeatures(
        offerWithStatusAndState,
      );

      const json = hasPreviewFeatures
        ? msftPrivateOfferToMsftUpdatePreviewOfferJSON(offerWithStatusAndState)
        : msftPrivateOfferToMsftUpdateOfferJSON(offerWithStatusAndState);

      const url = hasPreviewFeatures
        ? `api/offer/${poId}/saas/preview`
        : `api/offer/${poId}/saas`;

      offerLogger?.info('sending update MSFT offer request', { json, url });

      return stateTrackingSubmit(
        OfferAPIKey.Offer,
        () => offersMsftApi(accessTokenProvider).patch(url, { json }),
        convertToMsftPrivateOffer,
        handleOfferSubmissionError(NewOfferOfferSubmissionErrorsKey),
      );
    },
    [stateTrackingSubmit, accessTokenProvider, handleOfferSubmissionError],
  );

  const cancelOffer = useCallback(async (poId: string) => {
    return;
  }, []);

  const sendBuyerInstructions = useCallback(
    async (poId: string) => {
      await stateTrackingSubmitWithEmptyResponse(
        OfferAPIKey.SendBuyerInstructions,
        () =>
          offersMsftApi(accessTokenProvider).post(
            `api/offer/${poId}/notification/send-buyer-instructions/email`,
          ),
      );
    },
    [stateTrackingSubmitWithEmptyResponse, accessTokenProvider],
  );

  const archiveOffer = useCallback(async (poId: string) => {
    return;
  }, []);

  const getPlans = useCallback(
    async (vendorId: string, productId: string) => {
      return stateTrackingGet(
        OfferAPIKey.Silent,
        () =>
          offersMsftApi(accessTokenProvider).get(
            `api/vendor/${vendorId}/getPlans/product/${productId}`,
          ),
        (v) => v,
      );
    },
    [stateTrackingGet, accessTokenProvider],
  );

  const getPlan = useCallback(
    async (
      vendorId: string,
      productId: string,
      planId: string,
    ): Promise<PlanDetails | null> => {
      return stateTrackingGet(OfferAPIKey.Silent, () =>
        offersMsftApi(accessTokenProvider).get(
          `api/vendor/${vendorId}/getPlan/product/${productId}/plan/${planId}`,
        ),
      );
    },
    [stateTrackingGet, accessTokenProvider],
  );

  const getSalesforceDataMapping = useCallback(
    async (
      offerType: OfferType,
      opportunityId: string,
      mappingId?: string,
    ): Promise<MsftPrivateOfferSalesforceDataMapping | null> => {
      const operation =
        offerType === OfferType.PartnerResale
          ? 'createPartnerOffer'
          : 'createDirectOffer';

      const url = mappingId
        ? `api/mapped-fields?crmId=${opportunityId}&mappingId=${mappingId}`
        : `api/mapped-fields?crmId=${opportunityId}&cloud=msft&domain=offer&operation=${operation}`;

      return stateTrackingGet(
        OfferAPIKey.SalesforceDataMapping,
        () => salesforceDataMapperApi(accessTokenProvider).get(url),
        convertToSalesforceDataMapping,
      );
    },
    [stateTrackingGet, accessTokenProvider],
  );

  return {
    getOffer,
    getOfferSilently,
    createOffer,
    updateOffer,
    cancelOffer,
    sendBuyerInstructions,
    archiveOffer,
    getPlans,
    getPlan,
    getSalesforceDataMapping,
  };
};

export default useMsftPrivateOfferApi;
